/**
 * Spieler-spezifische Initialisierung beim Spielstart.
 * FogState laden, Planeten-Panels registrieren.
 */
import type { Engine } from './Engine';
import type { Camera } from '../renderer/Camera';
import type { SupabaseClient } from '../network/SupabaseClient';
import type { WorldManager } from '../world/WorldManager';
import type { BuildingType } from '../config/BuildingTypes';
import { ALL_BUILDING_TYPES } from '../config/BuildingTypes';
import { NoPlanetOverlay } from '../ui/NoPlanetOverlay';
import { PlanetListPanel } from '../ui/PlanetListPanel';
import { PlanetDetailPanel } from '../ui/PlanetDetailPanel';
import { PlanetMenu } from '../ui/PlanetMenu';
import { parseTickPlanetToCache, type PlanetHubCache } from './HubDataCache';
import { SunDetailPanel } from '../ui/SunDetailPanel';
import { getWorldPlanets } from '../world/PlayerWorldService';
import { SolarEnergySystem } from '../systems/SolarEnergySystem';
import { defaultSolarEnergyConfig } from '../config/SolarEnergyConfig';

/**
 * Initialisiert FogState und registriert Planeten-Module.
 * Lädt Fog-Reveals aus der DB (oder lokales Reveal im Dev-Modus).
 */
export async function setupPlayerState(
  engine: Engine, supabase: SupabaseClient,
  world: WorldManager, camera: Camera,
  hexRadius: number, worldId: string,
  playerId: string | undefined, startQ: number, startR: number,
): Promise<void> {
  if (world.fogState && playerId) {
    world.fogState.setPlayerId(playerId);
    await world.fogState.loadReveals();
  } else if (world.fogState) {
    world.fogState.addReveal(startQ, startR, 8);
  }

  const allPlanets = await getWorldPlanets(supabase, worldId, playerId);
  const devMode = !playerId;

  if (devMode) {
    for (const p of allPlanets) {
      p.feronium = 500;
      p.chrysalium = 500;
      p.neurogel = 500;
    }
  }

  const ownPlanets = allPlanets.filter((p) => p.ownership === 'own');
  const hubCache = new Map<string, PlanetHubCache>();

  if (!devMode) {
    await Promise.all(ownPlanets.map(async (p) => {
      const { data } = await supabase.functions.invoke('tick-planet', {
        body: { planet_id: p.id },
      });
      if (data?.resources) {
        p.feronium = data.resources.feronium;
        p.chrysalium = data.resources.chrysalium;
        p.neurogel = data.resources.neurogel;
      }
      if (data) hubCache.set(p.id, parseTickPlanetToCache(data));
    }));
  } else {
    for (const p of ownPlanets) {
      const levels = new Map<BuildingType, number>();
      for (const t of ALL_BUILDING_TYPES) levels.set(t, 0);
      hubCache.set(p.id, {
        buildings: { levels, timers: new Map() },
        fleet: { composition: [0, 0, 0, 0, 0, 0, 0, 0], levels },
      });
    }
  }

  const planetCounts = new Map<string, number>();
  const luminosities = new Map<string, number>();
  for (const p of allPlanets) {
    if (p.starSystemId) {
      planetCounts.set(p.starSystemId, (planetCounts.get(p.starSystemId) ?? 0) + 1);
      if (!luminosities.has(p.starSystemId)) luminosities.set(p.starSystemId, p.sunLuminosity);
    }
  }

  const solarCfg = defaultSolarEnergyConfig();
  engine.register(new SolarEnergySystem(solarCfg, allPlanets));
  engine.register(new PlanetDetailPanel(allPlanets, camera, hexRadius, devMode));
  engine.register(new PlanetMenu(devMode ? null : supabase, devMode, hubCache));
  engine.register(new SunDetailPanel(planetCounts, luminosities, solarCfg, camera, hexRadius, devMode));

  if (!playerId) return;

  if (ownPlanets.length > 0) {
    engine.register(new PlanetListPanel(camera, hexRadius, ownPlanets));
  } else {
    engine.register(new NoPlanetOverlay());
  }
}
